import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import { formatDate } from "../../utils/formatDate";
import {
  PLAN_NAMES,
  PLANS_PATH,
  accessEndsOnCancel,
  changeTakesEffectNow,
  isMonthly,
  maxSavePercent,
  minimumTermLine,
  periodLabel,
} from "../../utils/pricing";
import BillingToggle from "../BillingToggle";

/**
 * Membership purchase and management.
 *
 * Plans, prices and benefits all come from the API rather than being written into
 * this file, so what a patient is shown is always what Stripe will charge them.
 *
 * Buying redirects to Stripe Checkout. Switching plans does not — the existing
 * subscription is edited and the difference settled automatically, so there is no
 * payment page to send anyone to.
 */

const api = (path) => API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + path;


const card = {
  padding: "24px",
  borderRadius: "10px",
  backgroundColor: "var(--card)",
  border: "1px solid var(--border)",
};

const heading = {
  fontFamily: "var(--font-heading)",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "var(--youro-charcoal)",
  margin: 0,
};

const coralButton = (disabled) => ({
  backgroundColor: "var(--youro-coral)",
  color: "#fff",
  border: "1px solid transparent",
  borderRadius: "10px",
  padding: "0 24px",
  height: "48px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "transform 0.2s ease-out",
});

const skyButton = (disabled) => ({
  backgroundColor: "transparent",
  color: "var(--youro-sky)",
  border: "1px solid var(--youro-sky)",
  borderRadius: "10px",
  padding: "0 20px",
  height: "40px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
});

const ghostButton = (disabled) => ({
  backgroundColor: "transparent",
  color: "var(--youro-charcoal)",
  border: "none",
  borderRadius: "10px",
  padding: "0 16px",
  height: "40px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
});

const MembershipPanel = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // `busy` drives the buttons and stays set through the Stripe redirect; the
  // guard is the lock against two clicks landing in the same frame.
  const [, guard] = useSubmitGuard();
  const [switching, setSwitching] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [billing, setBilling] = useState("annual");

  const userId = Cookies.get(COOKIE_KEYS.userId);

  const load = useCallback(async () => {
    try {
      const [planRes, subRes] = await Promise.all([
        axios.get(api(PLANS_PATH)),
        userId ? axios.get(api(`/getSubscriptionDetails/${userId}`)) : Promise.resolve({ data: null }),
      ]);
      setPlans(planRes.data || []);
      setSubscription(subRes.data || null);
    } catch (err) {
      toast.error("We couldn't load your membership options. Refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const planLabel = (plan) => PLAN_NAMES[plan.name] || plan.name;
  const activePlan = plans.find((p) => p.planId === subscription?.planID);
  const hasMembership = Boolean(subscription?.isActive) && activePlan?.recurring;

  // Monthly and yearly versions of each membership are offered side by side;
  // the toggle picks which are shown. One-time passes are shown either way.
  const hasMonthly = plans.some(isMonthly);
  const showing = hasMonthly ? billing : "annual";
  const inBilling = (p) => !p.recurring || isMonthly(p) === (showing === "monthly");
  const toggle = hasMonthly ? (
    <div style={{ marginTop: "16px" }}>
      <BillingToggle value={showing} onChange={(v) => { setBilling(v); setSelectedPlanId(null); }} savePercent={maxSavePercent(plans)} />
    </div>
  ) : null;

  // A monthly membership still inside its minimum term ends on the minimum's
  // last day if cancelled now, not at the end of the month paid for.
  const minimumTermEnds = subscription?.minimumTermEnds || null;
  // Compared with today's local date: the UTC date runs a day ahead every
  // evening in the US, which ended the minimum term a few hours early.
  const inMinimumTerm = Boolean(minimumTermEnds) && minimumTermEnds >= new Date().toLocaleDateString("en-CA");
  // Cancelled inside the minimum: Stripe keeps billing monthly until the
  // minimum ends (StripeService.cancelSubscription), so "nothing further will
  // be charged" would be false. For such a membership the server reports
  // endDate as the minimum's end (SubscriptionService.getSubscriptionDetails),
  // so the two are equal; cancelled after the minimum, endDate is later.
  const billedUntilMinimum = Boolean(subscription?.cancelPending) && Boolean(minimumTermEnds)
    && (!subscription?.endDate || minimumTermEnds >= subscription.endDate);

  const startCheckout = guard(async () => {
    if (!selectedPlanId || !userId) return;
    setBusy(true);
    try {
      const res = await axios.post(
        api(`/payment/create-checkout-session?userId=${encodeURIComponent(userId)}&planId=${encodeURIComponent(selectedPlanId)}`)
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("We couldn't open the payment page. Try again in a moment.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't start checkout. Try again in a moment."));
      setBusy(false);
    }
  });

  const switchPlan = guard(async (planId) => {
    setBusy(true);
    try {
      const res = await axios.post(
        api(`/payment/change-plan?userId=${encodeURIComponent(userId)}&planId=${encodeURIComponent(planId)}`)
      );
      toast.success(res.data?.message || "Your membership has been updated.");
      setSwitching(false);
      setSelectedPlanId(null);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't change your membership. Try again, or contact support if it keeps happening."));
    } finally {
      setBusy(false);
    }
  });

  const cancelMembership = guard(async () => {
    setBusy(true);
    try {
      await axios.post(api(`/payment/cancel-subscription?userId=${encodeURIComponent(userId)}`));
      // Name the date when we know it: "the end of the period you've paid for"
      // is the sentence a patient reads as "so when, exactly?"
      const until = formatDate(accessEndsOnCancel(subscription), "");
      toast.success(
        until
          ? `Your membership is cancelled. You'll keep access until ${until}, and you can restart it here anytime.`
          : "Your membership is cancelled. You'll keep access until the end of the period you've paid for, and you can restart it here anytime."
      );
      setConfirmingCancel(false);
      setSelectedPlanId(null);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't cancel your membership. Try again, or contact support and we'll cancel it for you."));
    } finally {
      setBusy(false);
    }
  });

  const PlanCard = ({ plan, selected, onSelect, note }) => (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect(plan.planId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(plan.planId);
        }
      }}
      style={{
        padding: "24px",
        borderRadius: "10px",
        backgroundColor: selected ? "var(--youro-mint)" : "var(--card)",
        border: selected ? "1px solid var(--youro-sage)" : "1px solid var(--border)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        minHeight: "44px",
      }}
    >
      <h4 style={{ ...heading, fontSize: "18px" }}>{planLabel(plan)}</h4>
      <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
        <span style={{ fontFamily: "var(--font-heading)", fontSize: "30px", fontWeight: 700, color: "var(--youro-charcoal)" }}>
          ${plan.price}
        </span>
        <span style={{ fontSize: "14px", color: "var(--youro-charcoal)" }}>
          {periodLabel(plan)}
        </span>
      </div>
      {minimumTermLine(plan) ? (
        <p style={{ margin: 0, fontSize: "14px", color: "var(--youro-muted)" }}>{minimumTermLine(plan)}</p>
      ) : null}
      <ul style={{ margin: "8px 0 0", paddingLeft: "20px", fontSize: "14px", lineHeight: 1.5, color: "var(--youro-charcoal)" }}>
        <li>{plan.diagnosisLimit > 1 ? "Multiple diagnoses treated" : "1 diagnosis treated"}</li>
        <li>
          {plan.numAppointments} video {plan.numAppointments === 1 ? "appointment" : "appointments"}
          {plan.recurring ? " a year" : ""}
          {isMonthly(plan) && plan.numAppointments >= 4 && plan.numAppointments % 4 === 0 ? `, ${plan.numAppointments / 4} every 3 months` : ""}
        </li>
        {plan.educationAccess ? <li>Curated educational content</li> : null}
      </ul>
      {note ? (
        <p style={{ margin: "8px 0 0", fontSize: "14px", fontWeight: 500, color: "var(--youro-sage)" }}>{note}</p>
      ) : null}
    </div>
  );

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
    marginTop: "24px",
  };

  if (loading) {
    return (
      <div style={{ ...card, marginBottom: "24px" }}>
        <h3 style={heading}>Membership</h3>
        <p style={{ marginTop: "8px", fontSize: "16px", color: "var(--youro-charcoal)" }}>Loading your plan…</p>
      </div>
    );
  }

  // Switching: show the other memberships with what the change will cost.
  if (hasMembership && switching) {
    return (
      <div style={{ ...card, marginBottom: "24px" }}>
        <h3 style={heading}>Change your membership</h3>
        <p style={{ marginTop: "8px", fontSize: "16px", lineHeight: 1.6, color: "var(--youro-charcoal)" }}>
          You're on {planLabel(activePlan)}, billed {isMonthly(activePlan) ? "monthly" : "yearly"}. Moving to a higher
          plan with the same billing charges only the difference for the rest of your current period and starts straight
          away. Any other change, including between monthly and yearly billing, takes effect on your renewal date. You
          keep everything you've paid for until then, and nothing is charged or refunded now.
        </p>
        <div style={grid}>
          {plans
            .filter((p) => p.recurring && p.planId !== activePlan.planId)
            .map((p) => {
              const diff = p.price - activePlan.price;
              const now = changeTakesEffectNow(activePlan, p);
              return (
                <PlanCard
                  key={p.planId}
                  plan={p}
                  selected={selectedPlanId === p.planId}
                  onSelect={setSelectedPlanId}
                  // A downgrade is scheduled rather than credited, so there is no
                  // figure to quote — nothing moves until the renewal date.
                  // The full price difference is the most an upgrade can cost:
                  // Stripe charges it only for the part of the year that is left.
                  // "About $X" presented that maximum as the likely charge.
                  note={now && diff > 0
                    ? `Up to $${diff} more \u2014 you pay only for the rest of your ${isMonthly(p) ? "month" : "year"}`
                    : "Starts at your next renewal"}
                />
              );
            })}
        </div>
        <div style={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "24px", flexWrap: "wrap" }}>
          <button
            type="button"
            style={coralButton(!selectedPlanId || busy)}
            disabled={!selectedPlanId || busy}
            onClick={() => switchPlan(selectedPlanId)}
          >
            {busy ? "Updating…" : "Confirm change"}
          </button>
          <button
            type="button"
            style={ghostButton(busy)}
            disabled={busy}
            onClick={() => {
              setSwitching(false);
              setSelectedPlanId(null);
            }}
          >
            Never mind
          </button>
        </div>
      </div>
    );
  }

  // Cancelled but still running. The membership stays active to the end of the
  // paid period, so without this branch the screen looks exactly as it did
  // before cancelling and offers the cancel button again.
  if (hasMembership && subscription?.cancelPending) {
    return (
      <div style={{ ...card, marginBottom: "24px" }}>
        <h3 style={heading}>Membership</h3>
        <div style={{ marginTop: "16px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "baseline" }}>
          <span style={{ fontFamily: "var(--font-heading)", fontSize: "24px", fontWeight: 600, color: "var(--youro-charcoal)" }}>
            {planLabel(activePlan)}
          </span>
          <span style={{
            padding: "2px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 500,
            backgroundColor: "var(--youro-sand)", color: "var(--youro-charcoal)",
          }}>
            Cancelled
          </span>
        </div>

        <p style={{ marginTop: "12px", fontSize: "16px", lineHeight: 1.6, color: "var(--youro-charcoal)" }}>
          {billedUntilMinimum ? (
            <>
              This membership ends on {formatDate(minimumTermEnds, "")}, when your minimum term is complete. Until then it
              renews monthly as usual, so you'll still be charged each month up to that date. Nothing is charged after it.
            </>
          ) : (
            <>
              This membership will not renew. You keep full access
              {formatDate(accessEndsOnCancel(subscription), "") ? ` until ${formatDate(accessEndsOnCancel(subscription), "")}` : " until the end of the period you've paid for"}.
              Nothing further will be charged.
            </>
          )}
        </p>

        <p style={{ marginTop: "16px", fontSize: "16px", fontWeight: 600, color: "var(--youro-charcoal)" }}>
          Changed your mind?
        </p>
        <p style={{ margin: "4px 0 0", fontSize: "14px", color: "var(--youro-charcoal)", opacity: 0.8 }}>
          Start a new membership on either plan. It begins when your current access ends.
        </p>
        {toggle}

        <div style={grid}>
          {plans.filter((p) => p.recurring && inBilling(p)).map((p) => (
            <PlanCard key={p.planId} plan={p} selected={selectedPlanId === p.planId} onSelect={setSelectedPlanId} />
          ))}
        </div>

        <div style={{ marginTop: "24px" }}>
          <button
            type="button"
            style={coralButton(!selectedPlanId || busy)}
            disabled={!selectedPlanId || busy}
            onClick={startCheckout}
          >
            {busy ? "Opening secure payment…" : "Restart membership"}
          </button>
          <p style={{ margin: "16px 0 0", fontSize: "14px", color: "var(--youro-charcoal)" }}>
            Payment is handled by Stripe. Your card details never reach Youro.
          </p>
        </div>
      </div>
    );
  }

  // Active membership: show it, and offer switch or cancel.
  if (hasMembership) {
    return (
      <div style={{ ...card, marginBottom: "24px" }}>
        <h3 style={heading}>Membership</h3>
        <div style={{ marginTop: "16px", display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "baseline" }}>
          <span style={{ fontFamily: "var(--font-heading)", fontSize: "24px", fontWeight: 600, color: "var(--youro-sage)" }}>
            {planLabel(activePlan)}
          </span>
          <span style={{ fontSize: "16px", color: "var(--youro-charcoal)" }}>
            ${activePlan.price}{periodLabel(activePlan)} · {subscription.appointmentsLeft ?? activePlan.numAppointments} appointments left
          </span>
        </div>
        {inMinimumTerm ? (
          <p style={{ marginTop: "12px", fontSize: "14px", lineHeight: 1.5, color: "var(--youro-charcoal)" }}>
            Your minimum term runs until {formatDate(minimumTermEnds, "")}. You can cancel anytime; if you do, your
            membership ends then.
          </p>
        ) : null}

        <div style={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "24px", flexWrap: "wrap" }}>
          <button type="button" style={skyButton(busy)} disabled={busy} onClick={() => setSwitching(true)}>
            Change plan
          </button>
          {confirmingCancel ? (
            <>
              <span style={{ fontSize: "14px", color: "var(--youro-charcoal)" }}>
                {inMinimumTerm
                  ? `Cancel your membership? You'll keep it, and keep paying monthly, until ${formatDate(minimumTermEnds, "")}.`
                  : "Cancel your membership?"}
              </span>
              {/* Coral is the one call to action on a screen, so it belongs on
                  the safe choice. It was on "Yes, cancel", which gave the
                  destructive, hard-to-undo option the visual weight and made
                  keeping the membership look like the secondary path. */}
              <button type="button" style={coralButton(busy)} disabled={busy} onClick={() => setConfirmingCancel(false)}>
                Keep it
              </button>
              <button type="button" style={ghostButton(busy)} disabled={busy} onClick={cancelMembership}>
                {busy ? "Cancelling…" : "Yes, cancel"}
              </button>
            </>
          ) : (
            <button type="button" style={ghostButton(busy)} disabled={busy} onClick={() => setConfirmingCancel(true)}>
              Cancel membership
            </button>
          )}
        </div>
      </div>
    );
  }

  // No membership: a one-time pass may still be active, which doesn't block buying one.
  const hasPass = Boolean(subscription?.isActive) && activePlan && !activePlan.recurring;

  return (
    <div style={{ ...card, marginBottom: "24px" }}>
      <h3 style={heading}>Choose your membership</h3>
      <p style={{ marginTop: "8px", fontSize: "16px", lineHeight: 1.6, color: "var(--youro-charcoal)" }}>
        {hasPass
          ? `Your ${planLabel(activePlan)} pass is still active. Starting a membership adds ongoing care on top of it.`
          : "Select the plan that fits your care, then continue to secure payment."}
      </p>
      {toggle}

      <div style={grid}>
        {plans.filter(inBilling).map((p) => (
          <PlanCard key={p.planId} plan={p} selected={selectedPlanId === p.planId} onSelect={setSelectedPlanId} />
        ))}
      </div>

      <div style={{ marginTop: "24px" }}>
        <button type="button" style={coralButton(!selectedPlanId || busy)} disabled={!selectedPlanId || busy} onClick={startCheckout}>
          {busy ? "Opening secure payment…" : "Continue to payment"}
        </button>
        <p style={{ margin: "16px 0 0", fontSize: "14px", color: "var(--youro-charcoal)" }}>
          Payment is handled by Stripe. Your card details never reach Youro.
        </p>
      </div>
    </div>
  );
};

export default MembershipPanel;
